// Phase 3.4 — Sales Return list table.
//
// Replaces the MUI <Table> + antd Dropdown/Menu action list in salesReturn.tsx
// with the shared shadcn DataTable, StatusBadge and a DropdownMenu. Presentation
// only: search + server pagination reuse the screen's handlers; the row action
// menu (only shown for statusRefId 4/11, as in the legacy) calls the screen's
// `handleEditOrDelete(item, id)` with the same '1'/'2' keys. ₹ amounts keep the
// same Intl currency formatter; the online-order/return/cancelled marker icons
// are preserved.
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVertical } from "lucide-react";
import moment from "moment";
import { DataTable } from "../../shared/data-table";
import { StatusBadge } from "../../shared/status-badge";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import AppConstant from "../../Globals/AppConstants";
import AppImages from "../../Globals/AppImages";

const Formatter = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

// statusRefId 2/5/11 are the real return actions (see RETURN_TYPES in
// SalesReturnBillFormSheet.tsx / STATUS_OPTIONS in SalesReturnFormSheet.tsx)
// — 4 is the legacy raw "Returned" status, still reachable only via editing
// an old pre-bill-first record.
const RETURN_TYPE_LABEL: Record<number, string> = { 2: "Normal Return", 5: "Damage Return", 11: "Cancel Return" };
// Cancel Return is no longer offered as a choice — only shown in
// RETURN_TYPE_LABEL/isLegacyRow below so any pre-existing cancelled rows
// still render and stay editable.
const RETURN_TYPE_FILTER_OPTIONS = [
  { value: "2", label: "Normal Return" },
  { value: "5", label: "Damage Return" },
];

// Filters on the same isCreditPayment flag the "Credit Bill" column shows —
// whether the original bill this return came from was paid via Credit.
const CREDIT_FILTER_OPTIONS = [
  { value: "credit", label: "Credit" },
  { value: "no_credit", label: "No Credit" },
];

export interface SalesReturnTableProps {
  data: any[];
  loading: boolean;
  onMenuAction: (item: any, id: string) => void;
  onInvoiceClick: (item: any) => void;
  searchValue?: string;
  onSearch: (query: string) => void;
  searchPlaceholder?: string;
  title?: React.ReactNode;
  headerActions?: React.ReactNode;
  pagination: { page: number; total: number; onChange: (page: number) => void };
  returnTypeFilter?: string;
  onReturnTypeFilterChange?: (value: string) => void;
  /** "" = All, "credit" = isCreditPayment true, "no_credit" = isCreditPayment false. */
  creditFilter?: string;
  onCreditFilterChange?: (value: string) => void;
}

export function SalesReturnTable({
  data,
  loading,
  onMenuAction,
  onInvoiceClick,
  searchValue,
  onSearch,
  searchPlaceholder,
  title,
  headerActions,
  pagination,
  returnTypeFilter,
  onReturnTypeFilterChange,
  creditFilter,
  onCreditFilterChange,
}: SalesReturnTableProps) {
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "productId", header: "Product Id", meta: { sticky: "left" }, cell: ({ row }) => row.original?.productId },
      { id: "categoryName", header: "Category", cell: ({ row }) => row.original?.categoryName },
      { id: "subCategoryName", header: "Sub Category", cell: ({ row }) => row.original?.subCategoryName },
      {
        id: "invoice",
        header: "Invoice Id",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <button
              type="button"
              onClick={() => onInvoiceClick(item)}
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              {item?.invoice}
              {item?.isOnlineOrder != 1 && <img src={AppImages.onlineOrderIcon} alt="" />}
            </button>
          );
        },
      },
      { id: "mrp", header: "Mrp", cell: ({ row }) => <span className="tabular-nums">{Formatter.format(row.original?.mrp)}</span> },
      { id: "returnedAmount", header: "Returned Amount", cell: ({ row }) => <span className="tabular-nums">{Formatter.format(row.original?.returnedAmount)}</span> },
      {
        id: "returnType",
        header: "Return Type",
        cell: ({ row }) => RETURN_TYPE_LABEL[row.original?.statusRefId] ?? "-",
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <span className="inline-flex items-center gap-1">
              <StatusBadge status={item?.status} />
              {item?.isOnlineOrder != 1 && (
                <img src={item?.statusRefId == 4 ? AppImages.onlineReturnIcon : AppImages.onlineCancelledIcon} alt="" />
              )}
            </span>
          );
        },
      },
      { id: "quantity", header: "Qty", cell: ({ row }) => <span className="tabular-nums">{row.original?.quantity}</span> },
      {
        id: "isCreditPayment",
        header: "Credit Bill",
        cell: ({ row }) =>
          row.original?.isCreditPayment ? (
            <Badge variant="secondary">Credit</Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        id: "createdBy",
        header: "Created By",
        cell: ({ row }) => (
          <span className="max-w-[120px] truncate block" title={row.original?.createdBy}>
            {row.original?.createdBy || "-"}
          </span>
        ),
      },
      {
        id: "createdOn",
        header: "Created On",
        cell: ({ row }) => (row.original?.createdOn ? moment(row.original?.createdOn).format("DD-MM-YY, hh:mm A") : "-"),
      },
      /* {
        id: "action",
        header: "Action",
        meta: { sticky: "right" },
        cell: ({ row }) => {
          const item = row.original;
          // Cancelled/legacy raw Returned rows (statusRefId 4/11) keep both
          // Edit and Delete; active Normal/Damage rows (statusRefId 2/5)
          // only get Edit — deleting one would need to reverse a stock
          // effect the row menu doesn't surface here (edit to Cancel Return
          // instead, which does reverse it).
          const isLegacyRow = item?.statusRefId == 4 || item?.statusRefId == 11;
          const isReturnTypeRow = item?.statusRefId == 2 || item?.statusRefId == 5;
          if (!isLegacyRow && !isReturnTypeRow) return null;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Row actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onMenuAction(item, "1")}>{AppConstant?.edit}</DropdownMenuItem>
                {isLegacyRow && (
                  <DropdownMenuItem onClick={() => onMenuAction(item, "2")}>{AppConstant?.Delete}</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      }, */
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <DataTable
      columns={columns}
      data={data || []}
      loading={loading}
      searchable
      searchValue={searchValue}
      onSearch={onSearch}
      searchPlaceholder={searchPlaceholder}
      title={title}
      toolbar={
        <div className="flex items-center gap-2">
          {onReturnTypeFilterChange && (
            <Select
              value={returnTypeFilter || "all"}
              onValueChange={(v) => onReturnTypeFilterChange(v === "all" ? "" : v)}
            >
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Return Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Return Types</SelectItem>
                {RETURN_TYPE_FILTER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {onCreditFilterChange && (
            <Select
              value={creditFilter || "all"}
              onValueChange={(v) => onCreditFilterChange(v === "all" ? "" : v)}
            >
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Credit" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {CREDIT_FILTER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      }
      headerActions={headerActions}
      pagination={pagination}
    />
  );
}

export default SalesReturnTable;
